import { useQueryErrorResetBoundary } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { Button, Text, tokens } from "@ui"
import { css, keyframes } from "dx-styles"
import type { FC, ReactNode } from "react"

const auraSpin = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const auraFlicker = keyframes({
  "0%, 100%": { transform: "scale(0.88)", opacity: 0.45 },
  "44%": { transform: "scale(1.02)", opacity: 0.8 },
  "48%": { transform: "scale(1)", opacity: 0.55 },
  "52%": { transform: "scale(1.02)", opacity: 0.8 },
})

const orbit = keyframes({
  from: { transform: "rotate(0deg)" },
  to: { transform: "rotate(360deg)" },
})

const twinkle = keyframes({
  "0%, 100%": { opacity: 0.12 },
  "50%": { opacity: 0.7 },
})

const wrapper = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space.sm,
  width: "100%",
  minHeight: "60vh",
  paddingBlock: tokens.space.lg,
  overflow: "hidden",
})

const auraOrbit = css({
  position: "absolute",
  insetBlockStart: "50%",
  insetInlineStart: "50%",
  width: "520px",
  height: "520px",
  marginBlockStart: "-290px",
  marginInlineStart: "-260px",
  pointerEvents: "none",
  animation: `${auraSpin} 48s linear infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const aura = css({
  width: "100%",
  height: "100%",
  filter: "blur(6px)",
  backgroundImage: [
    "radial-gradient(46% 46% at 34% 62%, rgba(255, 55, 91, 0.7) 0%, rgba(255, 55, 91, 0.3) 36%, rgba(255, 55, 91, 0) 72%)",
    "radial-gradient(38% 38% at 64% 72%, rgba(255, 153, 3, 0.45) 0%, rgba(255, 153, 3, 0) 74%)",
    "radial-gradient(44% 44% at 68% 36%, rgba(126, 71, 151, 0.5) 0%, rgba(126, 71, 151, 0.22) 42%, rgba(126, 71, 151, 0) 76%)",
    "radial-gradient(40% 40% at 32% 34%, rgba(70, 40, 83, 0.35) 0%, rgba(70, 40, 83, 0) 78%)",
  ].join(", "),
  animation: `${auraFlicker} 9s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const stage = css({
  position: "relative",
  width: "220px",
  height: "220px",
})

const moteField = css({
  position: "absolute",
  inset: 0,
  overflow: "visible",
})

const motes = css({
  transformOrigin: "110px 110px",
  animation: `${orbit} 30s linear infinite reverse`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
})

const mote = css({
  animation: `${twinkle} 4.2s ease-in-out infinite`,
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 0.35,
  },
})

const moteDelayMid = css({ animationDelay: "1.4s" })
const moteDelayLate = css({ animationDelay: "2.8s" })

const copy = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: tokens.space.xs,
  maxWidth: "42ch",
  textAlign: "center",
})

const detail = css({
  maxWidth: "100%",
  marginBlockStart: tokens.space.xs,
  paddingBlock: tokens.space.xs,
  paddingInline: tokens.space.sm,
  borderRadius: tokens.radius.sm,
  backgroundColor: tokens.color.hover,
  overflowWrap: "anywhere",
})

const actions = css({
  display: "flex",
  gap: tokens.space.sm,
  marginBlockStart: tokens.space.sm,
})

export type IllustratedErrorStateProps = {
  title: string
  description: string
  error?: Error
  action?: ReactNode
  children: ReactNode
}

export const IllustratedErrorState: FC<IllustratedErrorStateProps> = ({
  title,
  description,
  error,
  action,
  children,
}) => {
  const router = useRouter()
  const { reset } = useQueryErrorResetBoundary()

  return (
    <div
      className={wrapper}
      role="alert"
    >
      <div className={auraOrbit}>
        <div className={aura} />
      </div>

      <div className={stage}>
        <svg
          viewBox="0 0 220 220"
          className={moteField}
          aria-hidden="true"
        >
          <g className={motes}>
            <circle
              className={mote}
              cx="110"
              cy="12"
              r="4"
              fill="#FF375B"
            />
            <circle
              className={`${mote} ${moteDelayMid}`}
              cx="194.9"
              cy="159"
              r="3"
              fill="#FF9903"
            />
            <circle
              className={`${mote} ${moteDelayLate}`}
              cx="25.1"
              cy="159"
              r="4"
              fill="#7E4797"
            />
          </g>
        </svg>
        {children}
      </div>

      <div className={copy}>
        <Text variant="h3">{title}</Text>
        <Text variant="description">{description}</Text>
        {error?.message ? (
          <div className={detail}>
            <Text
              variant="description"
              whiteSpace="breakSpace"
            >
              {error.message}
            </Text>
          </div>
        ) : undefined}
        <div className={actions}>
          <Button
            variant="primary"
            onClick={() => {
              reset()
              void router.invalidate()
            }}
          >
            Try again
          </Button>
          {action}
        </div>
      </div>
    </div>
  )
}
